import React from 'react';
import styles from './Bento.module.css';

export type BentoContentProps = {
  padLeftMobile?: number;
  padRightMobile?: number;
  padTopMobile?: number;
  padLeftDesktop?: number;
  padRightDesktop?: number;
  padTopDesktop?: number;
  contentMaxWidth?: string;
  title?: string;
  description?: string;
};

export type BentoItemProps = {
  title: string;
  description: string;
  image?: React.ReactNode;
  layout?: 'row' | 'column';
  colSpanLg?: number;
  subtitleAfterImage?: boolean;
  padBottom?: number;
  padBottomDesktop?: number;
  padLeft?: number;
  padRight?: number;
  padTop?: number;
  backgroundColor?: string;
  backgroundImage?: string;
  backgroundImageMobile?: string;
  backgroundImageDesktop?: string;
  backgroundPosition?: string;
  backgroundSize?: string;
  backgroundRepeat?: string;
  titleColor?: string;
  descriptionColor?: string;
  aspectRatio?: string;
  aspectRatioMobile?: string;
  contentMaxWidth?: string;
  customContent?:
    | React.ReactNode
    | ((props: BentoContentProps) => React.ReactNode);
};

export type BentoGridProps = {
  items: BentoItemProps[];
  className?: string;
};

function computeTextPadding(
  pads: Partial<Record<'left' | 'right' | 'top', number>>,
) {
  const base = { mobile: 32, desktop: 48 } as const;
  const sides: Array<'left' | 'right' | 'top'> = ['left', 'right', 'top'];

  const mobile = sides.reduce<
    Record<'left' | 'right' | 'top', number | undefined>
  >(
    (acc, side) => {
      const cardPad = pads[side];
      acc[side] =
        typeof cardPad === 'number'
          ? Math.max(0, base.mobile - cardPad)
          : undefined;
      return acc;
    },
    { left: undefined, right: undefined, top: undefined },
  );

  const desktop = sides.reduce<
    Record<'left' | 'right' | 'top', number | undefined>
  >(
    (acc, side) => {
      const cardPad = pads[side];
      acc[side] =
        typeof cardPad === 'number'
          ? Math.max(0, base.desktop - cardPad)
          : undefined;
      return acc;
    },
    { left: undefined, right: undefined, top: undefined },
  );

  return { mobile, desktop };
}

export const BentoGrid: React.FC<BentoGridProps> = ({ items, className }) => {
  return (
    <div className={[styles.grid, className].filter(Boolean).join(' ')}>
      {items.map(
        (
          {
            title: itemTitle,
            description,
            image,
            layout,
            colSpanLg,
            subtitleAfterImage,
            padBottom,
            padBottomDesktop,
            padLeft,
            padRight,
            padTop,
            backgroundColor,
            backgroundImage,
            backgroundImageMobile,
            backgroundImageDesktop,
            backgroundPosition,
            backgroundSize,
            backgroundRepeat,
            titleColor,
            descriptionColor,
            aspectRatio,
            aspectRatioMobile,
            contentMaxWidth,
            customContent,
          },
          index,
        ) => {
          const { mobile, desktop } = computeTextPadding({
            left: padLeft,
            right: padRight,
            top: padTop,
          });

          const bgImgMobile = backgroundImageMobile ?? backgroundImage;
          const bgImgDesktop = backgroundImageDesktop ?? backgroundImage;
          const aspectM = aspectRatioMobile ?? aspectRatio;

          const cardStyle = {
            ['--col-span-lg' as any]: String(colSpanLg || 1),
            ...(backgroundColor != null && {
              ['--bg-color' as any]: backgroundColor,
            }),
            ...(bgImgMobile && {
              ['--bg-img-mobile' as any]: `url(${bgImgMobile})`,
            }),
            ...(bgImgDesktop && {
              ['--bg-img-desktop' as any]: `url(${bgImgDesktop})`,
            }),
            ...(backgroundPosition != null && {
              ['--bg-pos' as any]: backgroundPosition,
            }),
            ...(backgroundSize != null && {
              ['--bg-size' as any]: backgroundSize,
            }),
            ...(backgroundRepeat != null && {
              ['--bg-repeat' as any]: backgroundRepeat,
            }),
            ...(aspectM != null && { ['--aspect-ratio-m' as any]: aspectM }),
            ...(aspectRatio != null && {
              ['--aspect-ratio-d' as any]: aspectRatio,
            }),
            ...(padLeft != null && { ['--pad-left' as any]: `${padLeft}px` }),
            ...(padRight != null && { ['--pad-right' as any]: `${padRight}px` }),
            ...(padBottom != null && {
              ['--pad-bottom' as any]: `${padBottom}px`,
            }),
            ...(padTop != null && { ['--pad-top' as any]: `${padTop}px` }),
            ...(padBottomDesktop != null && {
              ['--pad-bottom-desktop' as any]: `${padBottomDesktop}px`,
            }),
          } as React.CSSProperties;

          const textStyle = {
            ...(contentMaxWidth != null && {
              ['--text-max-width' as any]: contentMaxWidth,
            }),
            ...(mobile.left != null && {
              ['--pad-left-m' as any]: `${mobile.left}px`,
            }),
            ...(mobile.right != null && {
              ['--pad-right-m' as any]: `${mobile.right}px`,
            }),
            ...(mobile.top != null && {
              ['--pad-top-m' as any]: `${mobile.top}px`,
            }),
            ...(desktop.left != null && {
              ['--pad-left-d' as any]: `${desktop.left}px`,
            }),
            ...(desktop.right != null && {
              ['--pad-right-d' as any]: `${desktop.right}px`,
            }),
            ...(desktop.top != null && {
              ['--pad-top-d' as any]: `${desktop.top}px`,
            }),
          } as React.CSSProperties;

          return (
            <div
              key={`${itemTitle}-${index}`}
              className={styles.card}
              role="article"
              aria-label={itemTitle}
              data-layout={layout || 'column'}
              data-pad-left={padLeft != null ? 'true' : undefined}
              data-pad-right={padRight != null ? 'true' : undefined}
              data-pad-bottom={padBottom != null ? 'true' : undefined}
              data-pad-top={padTop != null ? 'true' : undefined}
              data-pad-bottom-desktop={
                padBottomDesktop != null ? 'true' : undefined
              }
              style={cardStyle}
            >
              {customContent ? (
                typeof customContent === 'function' ? (
                  customContent({
                    padLeftMobile: mobile.left,
                    padLeftDesktop: desktop.left,
                    padRightMobile: mobile.right,
                    padRightDesktop: desktop.right,
                    padTopMobile: mobile.top,
                    padTopDesktop: desktop.top,
                    contentMaxWidth,
                    title: itemTitle,
                    description,
                  })
                ) : (
                  customContent
                )
              ) : (
                <div className={styles.text} style={textStyle}>
                  <h3
                    className={styles.cardTitle}
                    style={
                      titleColor != null
                        ? ({ ['--title-color' as any]: titleColor } as React.CSSProperties)
                        : undefined
                    }
                  >
                    {itemTitle}
                  </h3>
                  {!subtitleAfterImage && (
                    <p
                      className={styles.cardSubtitle}
                      style={
                        descriptionColor != null
                          ? ({ ['--desc-color' as any]: descriptionColor } as React.CSSProperties)
                          : undefined
                      }
                    >
                      {description}
                    </p>
                  )}
                </div>
              )}
              {image}
              {subtitleAfterImage && !customContent && (
                <p
                  className={styles.cardSubtitle}
                  style={
                    {
                      gridColumn: '1 / -1',
                      paddingBottom:
                        padBottom != null ? `${padBottom}px` : undefined,
                      ...(descriptionColor != null && {
                        ['--desc-color' as any]: descriptionColor,
                      }),
                    } as React.CSSProperties
                  }
                >
                  {description}
                </p>
              )}
            </div>
          );
        },
      )}
    </div>
  );
};

export default BentoGrid;
